import React, { Fragment, useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { compose } from 'recompose';
import { withAnalytic } from '@skooldio/analytics-provider/dist/Analytics';

import LiveCodingModal from 'Containers/LiveCodingModal';
import { useConfig } from 'Context/ConfigContext';
import { useClassroomContext, LAYOUT_TYPE, LEARNING_MODE } from 'Context/ClassroomContext';
import CourseCreatePasswordModal from 'Routes/Classroom/Container/CourseCreatePasswordModal';
import postMessageHelper from 'Util/Message/postMessageHelper';
import { TOPIC } from 'Util/Message/TopicName';

import ComingSoon from '../ComingSoon';
import {
  MATERIAL_TYPE,
  VIDEO_ERROR_TYPE,
  VIDEO_PROVIDER,
  SUBSECTION_TYPE,
} from '../../../../Components/Classroom/Content';
import ContentWrapper from '../../../../Components/Classroom/ContentWrapper';
import {
  ClassroomSubsection,
  ClassroomInformation,
  useClassroomCourseData,
} from '../../../../Domain/Classroom';
import { withCapacitor } from '../../../../Util/Capacitors/hoc';
import { CLASSROOM_ACTIONS, genClassroomEvent } from '../../../../Util/Analytics';
import ClassroomModal from '../../Container/ClassroomModal';

/**
 * @param {Array<(ContentAsset|null|undefined)>} subsectionContentAssets
 * @param {ContentType} subsectionContentType
 * @returns {Array<(ContentAsset|null|undefined)>}
 */
const combineAssetsByContentType = (subsectionContentAssets = [], subsectionContentType) => {
  if (!Array.isArray(subsectionContentAssets) || !subsectionContentAssets.length) {
    return [];
  }

  if (subsectionContentType === MATERIAL_TYPE.VIDEO_SLIDE) {
    const ASSETS_PRIORITY = [MATERIAL_TYPE.VIDEO, MATERIAL_TYPE.YOUTUBE];
    let slideAssets = [],
      videoAssets = [],
      otherAssets = [];

    subsectionContentAssets.forEach((asset) => {
      if (asset?.type === MATERIAL_TYPE.YOUTUBE || asset?.type === MATERIAL_TYPE.VIDEO) {
        videoAssets.push(asset);
        return;
      }

      if (asset?.type === MATERIAL_TYPE.SLIDES) {
        slideAssets.push(asset);
        return;
      }

      otherAssets.push(asset);
    });

    //sort by content priority
    videoAssets.sort(
      (item, nextItem) =>
        ASSETS_PRIORITY.indexOf(item.type) - ASSETS_PRIORITY.indexOf(nextItem.type)
    );

    const videoAsset = videoAssets?.[0] ?? {};
    const slideAsset = slideAssets?.[0] ?? {};
    return [
      {
        content: {
          type: videoAsset?.type ?? null,
          ...(videoAsset?.content ?? {}),
          ...(slideAsset?.content ?? {}),
        },
        title: videoAsset?.title,
        type: MATERIAL_TYPE.VIDEO_SLIDE,
        id: 'video-slide',
        referId: slideAsset.referId,
      },
      ...otherAssets,
    ];
  }

  return subsectionContentAssets;
};

/**
 * @param {Array<(ContentAsset|null|undefined)>} subsectionContentAssets
 * @param {LearningMode} selectedLearningMode
 * @param {ContentType} subsectionContentType
 * @returns {Array<(ContentAsset|null|undefined)>}
 */
const adjustAssetsByLearningMode = (
  subsectionContentAssets = [],
  selectedLearningMode,
  subsectionContentType
) => {
  const _subsectionContentAssets = [...subsectionContentAssets];
  if (selectedLearningMode === LEARNING_MODE.DEFAULT) return _subsectionContentAssets;

  let supportedLearningModeAssetIndex = -1;
  if (subsectionContentType === SUBSECTION_TYPE.VIDEO) {
    supportedLearningModeAssetIndex = _subsectionContentAssets.findIndex(
      (asset) => asset.type === MATERIAL_TYPE.VIDEO
    );
  } else if (subsectionContentType === SUBSECTION_TYPE.QUIZ) {
    supportedLearningModeAssetIndex = _subsectionContentAssets.findIndex(
      (asset) => asset.type === MATERIAL_TYPE.QUIZ
    );
  }

  if (supportedLearningModeAssetIndex < 0) return _subsectionContentAssets;

  const supportedLearningModeAsset = _subsectionContentAssets[supportedLearningModeAssetIndex];
  const matchedLearningModeAssetContent = supportedLearningModeAsset.content.subContents?.find(
    (subContent) => subContent.learningMode === selectedLearningMode
  );
  if (matchedLearningModeAssetContent) {
    const matchedLearningModeAsset = {
      ...supportedLearningModeAsset,
      content: matchedLearningModeAssetContent,
    };
    _subsectionContentAssets[supportedLearningModeAssetIndex] = matchedLearningModeAsset;
  }

  return _subsectionContentAssets;
};

const getBase64FromBlob = (blob) => {
  return new Promise((resolve, _) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.readAsDataURL(blob);
  });
};

const isEncoded = (str) => {
  return str.includes('%');
};

const SubsectionContent = ({ allowedTypes, onExitClassroom, t, onLogAction, capacitorContext }) => {
  const {
    updateLayout,
    courseData: { courseTitle, courseSourceType },
    currentSubsectionId,
    currentLearningMode,
  } = useClassroomContext();

  const [needsRerender, setNeedsRerender] = useState(false);
  const [downloadableLink, setDownloadableLink] = useState(null);
  const { isClassroomTrialMode } = useClassroomCourseData();
  const { classroom, enableCreatePasswordModal } = useConfig();
  const disableWarningModal = classroom?.liveCoding?.disableWarningModal;
  const downloadAsFlutter = classroom?.material?.downloadAsFlutter;
  const isLearnCraftNewLayout = courseSourceType === 'LEARNCRAFT';

  useEffect(() => {
    if (needsRerender) {
      setNeedsRerender(false);
    }
  }, [needsRerender]);

  useEffect(() => {
    setNeedsRerender(true);
  }, [currentSubsectionId]);

  const {
    getAppState,
    isNative,
    origin: videoOrigin,
    checkInvalidDevice,
    openUrl,
  } = capacitorContext;
  const appState = getAppState();
  const delaySetCurrentTime = isNative ? 1000 : undefined;

  const updateLayoutByContent = (contentType, contentAssets) => {
    let layoutType;
    switch (contentType) {
      case MATERIAL_TYPE.EBOOK:
        layoutType = LAYOUT_TYPE.EBOOK;
        break;
      case MATERIAL_TYPE.QUIZ:
        layoutType = LAYOUT_TYPE.QUIZ;
        break;
      case MATERIAL_TYPE.VIDEO:
        if (
          contentAssets.some((asset) =>
            [VIDEO_PROVIDER.MINDSTAMP, VIDEO_PROVIDER.LEARNCRAFT].includes(asset.content?.provider)
          )
        ) {
          layoutType = LAYOUT_TYPE.EXTERNAL_VIDEO_PLAYER;
        } else {
          layoutType = LAYOUT_TYPE.DEFAULT;
        }
        break;
      default:
        layoutType = LAYOUT_TYPE.DEFAULT;
        break;
    }
    updateLayout(layoutType);
  };

  const checkInvalidDeviceByCapacitor = (classroomCourseId, courseId, courseCode) => async () => {
    const logRootedDeviceAction = () => {
      onLogAction(genClassroomEvent(CLASSROOM_ACTIONS.ROOTED_DEVICE, classroomCourseId));
      postMessageHelper(TOPIC.CLASSROOM_USE_ROOTED_DEVICE, { courseId, courseCode });
    };
    const logEmulatorAction = () => {
      onLogAction(genClassroomEvent(CLASSROOM_ACTIONS.EMULATOR, classroomCourseId));
      postMessageHelper(TOPIC.CLASSROOM_USE_EMULATOR, { courseId, courseCode });
    };
    const logScreenRecordAction = () => {
      onLogAction(genClassroomEvent(CLASSROOM_ACTIONS.RECORD_SCREEN, classroomCourseId));
      postMessageHelper(TOPIC.CLASSROOM_RECORD_SCREEN, { courseId, courseCode });
    };
    const logMultiScreenAction = () => {
      onLogAction(genClassroomEvent(CLASSROOM_ACTIONS.MULTIPLE_SCREEN, classroomCourseId));
      postMessageHelper(TOPIC.CLASSROOM_MULTIPLE_SCREEN, { courseId, courseCode });
    };
    await checkInvalidDevice({
      logRootedDeviceAction,
      logEmulatorAction,
      logScreenRecordAction,
      logMultiScreenAction,
    });
  };

  const onVideoError = (error) => {
    onLogAction(genClassroomEvent(CLASSROOM_ACTIONS.VIDEO_ERROR, error.code));
    if (error.code !== VIDEO_ERROR_TYPE.MEDIA_ERR_SRC_NOT_SUPPORTED) {
      setNeedsRerender(true);
    }
  };

  return (
    <ClassroomSubsection errorChecker>
      {({
        classroomCourseId,
        courseId,
        courseRevision,
        currentSubsectionId,
        title,
        description,
        contentType,
        contentAssets,
        materials,
        userDisplayName,
        userPhoneNumber,
        classroomSubSectionStatus,
        shouldShowReview,
        error,
        isEnableVideoResume,
        generateVideoSignedUrl,
        generateLiveSignedUrl,
        generateClassroomAssetSignedUrls,
        updateOnClassroomComplete,
        updateSubSectionData,
        updateClassroomTime,
        updateQuizSimpleResult,
        userProfileData,
        getDownloadlink,
        currentSubsectionReferId,
        isComingSoon,
      }) => {
        if (!currentSubsectionId) return <div>No Content available. Please select subsection.</div>;

        const handleOnDownload = async (filePath, assetId) => {
          if (!filePath) {
            setDownloadableLink(null);
            return;
          }

          const downloadUrl = await getDownloadlink(filePath, assetId);
          let fileName = downloadUrl.split('/').pop().split('#')[0].split('?')[0];
          if (isEncoded(fileName)) {
            // File name is encoded twice.
            fileName = decodeURIComponent(decodeURIComponent(fileName));
          }

          const blob = await fetch(downloadUrl).then((res) => res.blob());
          const downloadBlobUrl = URL.createObjectURL(blob);

          if (downloadAsFlutter) {
            const base64 = await getBase64FromBlob(blob);
            const base64ContentArray = base64.split(',');
            const decodedFile = base64ContentArray[1];
            window.flutter_inappwebview?.callHandler('downloadMaterial', fileName, decodedFile);
            return;
          } else {
            openUrl(downloadUrl, {
              asBrowser: true,
              defaultFunction: async () => {
                const downloadElement = document.createElement('a');

                downloadElement.href = downloadBlobUrl;
                downloadElement.download = fileName;

                downloadElement.click();
                downloadElement.remove();
              },
            });
          }
        };

        const handleUpdateQuizSimpleResult = (index, isCorrect, answeredChoiceIndex, quizId) => {
          const variables = {
            classroomId: classroomCourseId,
            subSectionReferId: currentSubsectionReferId,
            quizId: quizId,
            quizItemData: [
              { id: index, order: index, answers: answeredChoiceIndex, isCorrect: isCorrect },
            ],
          };
          updateQuizSimpleResult(variables);
        };

        updateLayoutByContent(contentType, contentAssets);

        const contents = combineAssetsByContentType(contentAssets, contentType);
        const learningModeContents = adjustAssetsByLearningMode(
          contents,
          currentLearningMode,
          contentType
        );

        if (isComingSoon) return <ComingSoon />;

        return (
          <ClassroomInformation>
            {({ progressPercent, courseCode }) =>
              !needsRerender && (
                <Fragment>
                  <ContentWrapper
                    courseId={courseId}
                    revision={courseRevision}
                    courseTitle={courseTitle}
                    courseProgress={progressPercent}
                    title={title}
                    description={description}
                    type={contentType}
                    items={learningModeContents}
                    getDownloadlink={getDownloadlink}
                    secureContent={{
                      video: generateVideoSignedUrl,
                      live: generateLiveSignedUrl,
                      assets: generateClassroomAssetSignedUrls,
                    }}
                    classroomCourseId={classroomCourseId}
                    onUpdateSubSectionData={updateSubSectionData}
                    onCompleted={updateOnClassroomComplete}
                    videoOverlayDisplayName={userDisplayName}
                    videoOverlayPhoneNumber={userPhoneNumber}
                    onVideoOverlayCorrupted={onExitClassroom}
                    subSectionStatus={classroomSubSectionStatus}
                    isResuming={isEnableVideoResume}
                    checkInvalidDevice={checkInvalidDeviceByCapacitor(
                      classroomCourseId,
                      courseId,
                      courseCode
                    )}
                    onUpdateClassroomTime={updateClassroomTime}
                    isAppStateInactive={appState ? appState.isActive : false}
                    onVideoError={onVideoError}
                    allowedTypes={allowedTypes}
                    delaySetCurrentTime={delaySetCurrentTime}
                    videoOrigin={videoOrigin}
                    userProfileData={userProfileData}
                    materials={materials}
                    onDownloadClick={handleOnDownload}
                    downloadUrl={downloadableLink}
                    onUpdateQuizSimpleResult={handleUpdateQuizSimpleResult}
                    onExitClassroom={onExitClassroom}
                    currentSubsectionReferId={currentSubsectionReferId}
                    isLearnCraftNewLayout={isLearnCraftNewLayout}
                  />
                  {!isClassroomTrialMode && (
                    <>
                      <ClassroomModal shouldShowReview={shouldShowReview} />
                      {enableCreatePasswordModal && <CourseCreatePasswordModal />}
                    </>
                  )}
                  {contentType === MATERIAL_TYPE.LIVECODING && !disableWarningModal && (
                    <LiveCodingModal />
                  )}
                </Fragment>
              )
            }
          </ClassroomInformation>
        );
      }}
    </ClassroomSubsection>
  );
};

SubsectionContent.propTypes = {
  onExitClassroom: PropTypes.func,
  allowedTypes: PropTypes.arrayOf(PropTypes.oneOf(Object.values(MATERIAL_TYPE))),
};
export default compose(withCapacitor, withAnalytic)(SubsectionContent);

/**
 * @typedef {import('Domain/Classroom/ClassroomSubsection').ClassroomCourseMaterial} ContentAsset
 * @typedef {import('Domain/Classroom/ClassroomSubsection').CLASSROOM_SUBSECTION_TYPE} ContentType
 * @typedef {import('Domain/Classroom/ClassroomSubsection').VideoMaterial} VideoMaterial
 * @typedef {import('Domain/Classroom/ClassroomSubsection').QuizMaterial} QuizMaterial
 * @typedef {import("Context/ClassroomContext/constant").LearningMode} LearningMode
 */
